import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Star,
  Shield,
  Heart,
  ArrowRight,
  Waves,
  Landmark,
  Mountain,
  Sparkles,
  ExternalLink,
  AlertTriangle,
  MessageSquareX,
  Users,
  Compass,
  CheckCircle2,
  PartyPopper,
} from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import TourCard from "@/components/TourCard";
import JsonLd from "@/components/JsonLd";
import PredictiveSearch from "@/components/PredictiveSearch";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [featuredTours, setFeaturedTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "en";

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const response = await axios.get(`${API}/tours?featured=true&lang=${lang}`);
        setFeaturedTours(response.data.slice(0, 4));
      } catch (error) {
        console.error("Error fetching tours:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, [lang]);

  const categories = [
    {
      id: "beach",
      name: t("home.cat_beach"),
      icon: Waves,
      color: "#006D77",
      description: t("home.cat_beach_desc"),
    },
    {
      id: "archaeological",
      name: t("home.cat_archaeological"),
      icon: Landmark,
      color: "#E29578",
      description: t("home.cat_archaeological_desc"),
    },
    {
      id: "adventure",
      name: t("home.cat_adventure"),
      icon: Mountain,
      color: "#2A9D8F",
      description: t("home.cat_adventure_desc"),
    },
    {
      id: "luxury",
      name: t("home.cat_luxury"),
      icon: Sparkles,
      color: "#83C5BE",
      description: t("home.cat_luxury_desc"),
    },
  ];

  const whyUs = [
    {
      icon: Shield,
      title: t("home.why_badge_vetted"),
      description: t("home.why_vetted_desc"),
    },
    {
      icon: Heart,
      title: t("home.why_badge_reviews"),
      description: t("home.why_honest_desc"),
    },
    {
      icon: Users,
      title: t("home.why_badge_adventurers"),
      description: t("home.why_local_desc"),
    },
  ];

  const painPoints = [
    { icon: AlertTriangle, label: t("home.pain_vendors") },
    { icon: MessageSquareX, label: t("home.pain_reviews") },
    { icon: Users, label: t("home.pain_crowds") },
  ];

  const steps = [
    {
      num: "1",
      icon: Compass,
      title: t("home.step1_title"),
      description: t("home.step1_desc"),
      image: "https://images.unsplash.com/photo-1518638150340-f706e86654de?w=400&q=60&fm=webp&fit=crop",
    },
    {
      num: "2",
      icon: CheckCircle2,
      title: t("home.step2_title"),
      description: t("home.step2_desc"),
      image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&q=60&fm=webp&fit=crop",
    },
    {
      num: "3",
      icon: PartyPopper,
      title: t("home.step3_title"),
      description: t("home.step3_desc"),
      image: "/cancun-beach-aerial.webp",
    },
  ];

  return (
    <div data-testid="home-page">
      <SEO
        title="Cancun Tours, Cenotes & Honest Reviews"
        description="Book the best Cancun tours: Chichen Itza, Xcaret, cenotes, Isla Mujeres & whale sharks. Honest reviews from locals — no sponsored fluff. Free cancellation 24h."
        path="/"
        lang={i18n.language?.startsWith("es") ? "es" : "en"}
        keywords="Cancun tours, Chichen Itza tour, cenote tours, Xcaret, Isla Mujeres, whale shark, Tulum, Cancun travel guide, honest reviews"
      />
      <JsonLd
        id="organization"
        data={{
          "@context": "https://schema.org",
          "@type": "TravelAgency",
          name: "Discover Cancun",
          url: SITE_URL,
          logo: `${SITE_URL}/logo-cropped.png`,
          description:
            "Honest reviews and curated tours for Cancun and the Riviera Maya — Chichen Itza, Xcaret, cenotes, Isla Mujeres and more.",
          areaServed: { "@type": "Place", name: "Cancun, Mexico" },
          sameAs: [],
        }}
      />
      <JsonLd
        id="website"
        data={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Discover Cancun",
          url: SITE_URL,
          inLanguage: ["en", "es"],
          potentialAction: {
            "@type": "SearchAction",
            target: `${SITE_URL}/tours?category={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        }}
      />
      {/* Hero Section */}
      <section className="hero-section relative" aria-label="Welcome to Discover Cancun">
        <div
          className="hero-bg"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1743697706311-120044ecaf22?w=1280&q=70&fm=webp&fit=crop)`,
          }}
          role="img"
          aria-label="Beautiful turquoise waters of Cancun beach"
        />

        <div className="container-custom relative z-10 pt-40 sm:pt-44 pb-12 sm:pb-20 px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="flex justify-center mb-4 sm:mb-6"
          >
            <picture>
              <source
                type="image/webp"
                srcSet="/logo-cropped-320.webp 320w, /logo-cropped-640.webp 640w, /logo-cropped.webp 800w"
                sizes="(max-width: 640px) 160px, (max-width: 768px) 288px, 320px"
              />
              <img
                src="/logo-cropped-320.webp"
                alt="Discover Cancun"
                data-testid="hero-logo"
                width="800"
                height="717"
                fetchpriority="high"
                decoding="async"
                className="w-40 sm:w-72 md:w-80 h-auto drop-shadow-2xl"
              />
            </picture>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="font-heading text-3xl sm:text-5xl md:text-6xl font-black text-[#1D3557] leading-tight mb-3 sm:mb-4">
              <span className="block">{t("home.hero_tagline_part1")}</span>
              <span className="block text-[#006D77]">{t("home.hero_tagline_part2")}</span>
            </h1>
            <p className="text-sm sm:text-base text-[#1D3557]/80 mb-6 sm:mb-8 max-w-xl mx-auto">
              {t("home.hero_subtitle_short")}
            </p>

            {/* Predictive Search Bar */}
            <div className="flex justify-center mb-5 sm:mb-6">
              <PredictiveSearch />
            </div>

            {/* Quick Category Chips */}
            <div className="flex flex-wrap justify-center gap-2 mb-8 sm:mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/tours?category=${cat.id}`}
                  data-testid={`hero-chip-${cat.id}`}
                  className="px-3 py-1.5 rounded-full bg-white/70 backdrop-blur-sm border border-white/60 text-xs sm:text-sm font-semibold text-[#1D3557] hover:bg-white hover:border-[#006D77] hover:text-[#006D77] transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-10 pt-5 sm:pt-6 border-t border-[#006D77]/20">
              <div>
                <p className="font-heading text-2xl sm:text-3xl font-bold text-[#006D77]">50+</p>
                <p className="text-xs text-[#64748B]">{t("home.stat_tours")}</p>
              </div>
              <div>
                <p className="font-heading text-2xl sm:text-3xl font-bold text-[#006D77]">1,200+</p>
                <p className="text-xs text-[#64748B]">{t("home.stat_travelers")}</p>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <Star className="w-5 h-5 sm:w-6 sm:h-6 text-[#F59E0B] fill-current" />
                <p className="font-heading text-2xl sm:text-3xl font-bold text-[#006D77]">4.8</p>
                <p className="text-xs text-[#64748B]">{t("home.stat_rating")}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pain Point Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-[#5B6E2F] to-[#7A8F3F]" data-testid="pain-section">
        <div className="container-custom px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-8 md:gap-12 items-center"
          >
            <div className="text-white">
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-5 leading-tight">
                {t("home.pain_title")}
              </h2>
              <p className="text-sm sm:text-base text-white/90 leading-relaxed mb-6">
                {t("home.pain_desc")}
              </p>
              <div className="flex flex-wrap gap-6 sm:gap-8">
                {painPoints.map((pp, i) => (
                  <div key={i} className="flex flex-col items-center text-center" data-testid={`pain-icon-${i}`}>
                    <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center mb-2 relative">
                      <pp.icon className="w-6 h-6 text-white" />
                      <span className="absolute -top-1 -right-1 text-red-400 text-lg font-bold">✕</span>
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-white">{pp.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3]">
              <picture>
                <source
                  type="image/webp"
                  media="(max-width: 640px)"
                  srcSet="/cramped-bus-600.webp"
                />
                <source type="image/webp" srcSet="/cramped-bus-900.webp" />
                <img
                  src="/cramped-bus-900.webp"
                  alt="Overcrowded tourist bus"
                  width="900"
                  height="1003"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </picture>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-12 sm:py-20 md:py-32" data-testid="categories-section">
        <div className="container-custom px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-12"
          >
            <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold text-[#1D3557] mb-3 sm:mb-4">
              {t("home.categories_title")}
            </h2>
            <p className="text-sm sm:text-lg text-[#64748B] max-w-2xl mx-auto px-4">
              {t("home.categories_subtitle")}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={`/tours?category=${category.id}`}
                  data-testid={`category-card-${category.id}`}
                  className="group block p-4 sm:p-6 md:p-8 bg-white rounded-2xl sm:rounded-3xl shadow-card hover-lift text-center"
                >
                  <div
                    className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-xl sm:rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110"
                    style={{ backgroundColor: `${category.color}15` }}
                  >
                    <category.icon
                      className="w-6 h-6 sm:w-8 sm:h-8"
                      style={{ color: category.color }}
                    />
                  </div>
                  <h3 className="font-heading font-semibold text-sm sm:text-lg text-[#1D3557] mb-0.5 sm:mb-1">
                    {category.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#64748B] hidden sm:block">{category.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Tours Section */}
      <section className="py-12 sm:py-20 md:py-32 bg-gradient-to-b from-transparent to-white/50" data-testid="featured-tours-section">
        <div className="container-custom px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end justify-between mb-8 sm:mb-12"
          >
            <div>
              <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold text-[#1D3557] mb-2 sm:mb-4">
                {t("home.featured_title")}
              </h2>
              <p className="text-sm sm:text-lg text-[#64748B] max-w-xl">
                {t("home.featured_subtitle")}
              </p>
            </div>
            <Link
              to="/tours"
              data-testid="view-all-tours-btn"
              className="btn-secondary mt-4 md:mt-0 inline-flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              {t("home.view_all")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden">
                  <div className="h-40 skeleton" />
                  <div className="p-4 space-y-3">
                    <div className="h-3 skeleton rounded w-1/3" />
                    <div className="h-4 skeleton rounded w-2/3" />
                    <div className="h-3 skeleton rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredTours.map((tour) => (
                <TourCard
                  key={tour.id}
                  tour={tour}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 sm:py-20 md:py-32" data-testid="why-us-section">
        <div className="container-custom px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-12 max-w-3xl mx-auto"
          >
            <span className="font-accent text-xl sm:text-2xl text-[#E29578] block mb-1 sm:mb-2">
              {t("home.why_us_accent")}
            </span>
            <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold text-[#1D3557] mb-3 sm:mb-4 leading-tight">
              {t("home.why_us_title")}
            </h2>
            <p className="text-sm sm:text-lg text-[#64748B]">
              {t("home.why_us_subtitle")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
            {whyUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="text-center p-4 sm:p-8 bg-white rounded-2xl shadow-card"
                data-testid={`why-us-card-${index}`}
              >
                <div className="w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full bg-[#006D77]/10 flex items-center justify-center">
                  <item.icon className="w-7 h-7 sm:w-10 sm:h-10 text-[#006D77]" />
                </div>
                <h3 className="font-heading font-bold text-lg sm:text-xl text-[#1D3557] mb-2 sm:mb-3">
                  {item.title}
                </h3>
                <p className="text-sm sm:text-base text-[#64748B] leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 Simple Steps Section */}
      <section className="py-12 sm:py-20 md:py-28 bg-gradient-to-br from-[#FFF8EC] to-[#FDEBC8]" data-testid="steps-section">
        <div className="container-custom px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-14"
          >
            <span className="font-accent text-xl sm:text-2xl text-[#E29578] block mb-1 sm:mb-2">
              {t("home.steps_accent")}
            </span>
            <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold text-[#1D3557] leading-tight">
              {t("home.steps_title")}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative bg-white rounded-2xl overflow-hidden shadow-card hover-lift"
                data-testid={`step-card-${step.num}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={step.image}
                    alt={step.title}
                    width="400"
                    height="250"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 left-3 w-10 h-10 rounded-lg bg-[#5B6E2F] text-white flex items-center justify-center font-heading font-bold text-lg shadow-lg">
                    {step.num}.
                  </div>
                </div>
                <div className="p-5 sm:p-6">
                  <h3 className="font-heading font-bold text-lg sm:text-xl text-[#1D3557] mb-2">
                    {step.num}. {step.title}:
                  </h3>
                  <p className="text-sm sm:text-base text-[#64748B] leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 md:py-32" data-testid="cta-section">
        <div className="container-custom px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-[#1D3557] p-6 sm:p-8 md:p-16"
          >
            <div className="absolute top-0 right-0 w-1/2 h-full opacity-20 hidden sm:block">
              <img
                src="https://images.unsplash.com/photo-1601902726356-c536ab69488f?w=800&q=60&fm=webp&fit=crop&auto=format"
                alt=""
                width="800"
                height="600"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10 max-w-xl">
              <span className="font-accent text-xl sm:text-2xl text-[#E29578] block mb-1 sm:mb-2">
                {t("home.cta_accent")}
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
                {t("home.cta_title")}
              </h2>
              <p className="text-sm sm:text-base text-white/70 mb-6 sm:mb-8">
                {t("home.cta_subtitle")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link
                  to="/tours"
                  data-testid="cta-browse-btn"
                  className="bg-[#E29578] hover:bg-[#d88567] text-white rounded-full px-6 sm:px-8 py-3 sm:py-4 font-bold transition-all text-center text-sm sm:text-base"
                >
                  {t("home.cta_browse")}
                </Link>
                <Link
                  to="/guide"
                  data-testid="cta-guide-btn"
                  className="bg-white/10 hover:bg-white/20 text-white rounded-full px-6 sm:px-8 py-3 sm:py-4 font-bold transition-all border border-white/20 text-center text-sm sm:text-base"
                >
                  {t("home.cta_guide")}
                </Link>
              </div>
              <p className="text-xs sm:text-sm text-white/50 italic mt-6 sm:mt-8 max-w-lg" data-testid="cta-disclaimer">
                *{t("home.cta_disclaimer")}*
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Affiliate Support Banner */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-gradient-to-r from-[#006D77] to-[#83C5BE]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {t("home.affiliate_title")}
            </h2>
            <p className="text-white/90 text-base sm:text-lg mb-6 max-w-2xl mx-auto">
              {t("home.affiliate_subtitle")}
            </p>
            <a
              href="https://www.viator.com/?pid=P00288918&mcid=42383&medium=link&medium_version=selector&campaign=Cancun-trips-and-excursions-tours"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="viator-affiliate-btn"
              aria-label={`${t("home.affiliate_btn")} (${t("a11y.opens_new_tab")})`}
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'affiliate_click', {
                    affiliate: 'viator_main',
                    location: 'home_banner'
                  });
                }
              }}
              className="inline-flex items-center gap-2 bg-white text-[#006D77] px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 hover:scale-105 transition-all shadow-lg"
            >
              {t("home.affiliate_btn")}
              <ExternalLink className="w-5 h-5" aria-hidden="true" />
            </a>
            <p className="text-white/70 text-sm mt-4">
              {t("home.affiliate_thanks")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-white" aria-label="About Cancun Tours">
        <div className="max-w-4xl mx-auto">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1D3557] mb-6">
              {t("home.seo_title")}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 text-[#457B9D]">
              <div>
                <h3 className="text-xl font-semibold text-[#1D3557] mb-3">{t("home.seo_attractions_title")}</h3>
                <p className="mb-4" dangerouslySetInnerHTML={{ __html: t("home.seo_attractions_p1") }} />
                <p className="mb-4" dangerouslySetInnerHTML={{ __html: t("home.seo_attractions_p2") }} />
                <p dangerouslySetInnerHTML={{ __html: t("home.seo_attractions_p3") }} />
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-[#1D3557] mb-3">{t("home.seo_tours_title")}</h3>
                <ul className="space-y-2">
                  <li dangerouslySetInnerHTML={{ __html: t("home.seo_adventure") }} />
                  <li dangerouslySetInnerHTML={{ __html: t("home.seo_beach") }} />
                  <li dangerouslySetInnerHTML={{ __html: t("home.seo_history") }} />
                  <li dangerouslySetInnerHTML={{ __html: t("home.seo_wildlife") }} />
                  <li dangerouslySetInnerHTML={{ __html: t("home.seo_nightlife") }} />
                  <li dangerouslySetInnerHTML={{ __html: t("home.seo_families") }} />
                </ul>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-[#EDF6F9] rounded-xl">
              <h3 className="text-xl font-semibold text-[#1D3557] mb-3">{t("home.seo_tips_title")}</h3>
              <div className="grid sm:grid-cols-3 gap-4 text-sm text-[#457B9D]">
                <div>
                  <strong className="text-[#006D77]">{t("home.seo_tips_visit_label")}</strong>
                  <p>{t("home.seo_tips_visit")}</p>
                </div>
                <div>
                  <strong className="text-[#006D77]">{t("home.seo_tips_money_label")}</strong>
                  <p>{t("home.seo_tips_money")}</p>
                </div>
                <div>
                  <strong className="text-[#006D77]">{t("home.seo_tips_transport_label")}</strong>
                  <p>{t("home.seo_tips_transport")}</p>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default Home;
